const React = require('react');
const { useEffect } = React;
const { connect } = require('react-redux');
const { context } = require('app-context');
const Frame = require('../../components/Chat/Frame');
const {
    canLoadClassChat,
    dependencyPropTypes,
    hasClassChatDependencies
} = require('./class-chat-dependencies');

const selectors = context.selectors.all;
const actions   = context.actions;
const redux     = context.redux.hooks;
const handlers  = context.handlers;

const internals = {};

internals.CLASS_DETAILS_FRESH_MS = 1000;

const addUserToNameMap = (collector, user) => {

    if (!user || user.id === undefined || user.id === null) {
        return collector;
    }

    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (name) {
        collector[user.id] = name;
    }

    return collector;
};

internals.ClassChatFrame = function ClassChatFrame(props) {

    const {
        canonicalSid,
        classDetailsFetchedAt,
        classId,
        history,
        match,
        onRefreshClass,
        showNotification,
        ...frameProps
    } = props;
    const routeSid = match.params.sid;
    const shouldRefreshClass = !classDetailsFetchedAt
        || Date.now() - classDetailsFetchedAt > internals.CLASS_DETAILS_FRESH_MS;
    const sidHasChanged = !!canonicalSid && canonicalSid !== routeSid;

    useEffect(() => {

        if (!shouldRefreshClass) {
            return undefined;
        }

        if (!classId) {
            return undefined;
        }

        onRefreshClass({ id: classId });
    }, [classId, onRefreshClass, shouldRefreshClass]);

    useEffect(() => {

        if (!sidHasChanged) {
            return;
        }

        showNotification('Group chat recovered.');
        history.replace(`/app/classes/chat/${canonicalSid}`);
    }, [canonicalSid, history, showNotification, sidHasChanged]);

    return (
        <Frame
            {...frameProps}
            classId={classId}
            history={history}
            key={frameProps.sid}
            match={match}
            showNotification={showNotification}
        />
    );
};

const ClassChatFrame = connect(
    (state, { match: { params } }) => {

        const groupUsers = selectors.getClassUsers_forChat(state, params.sid) || [];
        const currentUser = selectors.getCurrentUser(state)?.user();
        const messages = selectors.getMessages_forChat(state, params.sid) || [];
        const usersNameMap = groupUsers.reduce(addUserToNameMap, {});
        const rolePermissions = selectors.getCurrentUserRolePermissions(state);
        const rolesInteractions = selectors.getCurrentUserRoleInteractions(state);

        messages.forEach((message) => addUserToNameMap(usersNameMap, message.user));
        addUserToNameMap(usersNameMap, currentUser);

        const classId = selectors.getClassId_forChat(state, params.sid) || selectors.getClassIdBySid(state, params.sid);
        const groupFromClass = classId && (
            selectors.getGroup_forAnnouncementChat?.(state, classId) ||
            selectors.getGroup_forGroupDetail?.(state, classId)?.class ||
            selectors.getGroup_forGroupDetail?.(state, classId) ||
            selectors.getGroup_forGroupDetail_withUserSearchResults?.(state, classId)?.class ||
            selectors.getGroup_forGroupDetail_withUserSearchResults?.(state, classId) ||
            selectors.getGroup_forGroupEdit?.(state, classId)
        );
        const group = selectors.getGroup_forChat(state, params.sid) || groupFromClass || (classId ? {
            id: classId,
            sid: params.sid
        } : {});

        const frameProps = {
            canonicalSid: selectors.getClassSidById(state, classId),
            classId,
            classDetailsFetchedAt: selectors.getGroupDetailsFetchedAt(state, classId),
            messages,
            hasUnreadMessages: selectors.hasUnreadMessages(state, params.sid),
            emptyChatInfo: 'There are no group messages yet.',
            users: groupUsers,
            usersNameMap,
            group,
            onSetLastReadMessageIndex: ({ messageIndex }) => redux.dispatch(actions.communication.setLastReadMessageIndex({ channelSid: params.sid, messageIndex })),
            onRequestMessages: ({ initial } = {}) => {

                const fetchMessages = initial
                    ? actions.dataFetching.fetchMessages.firstPage
                    : actions.dataFetching.fetchMessages.nextPage;

                return redux.dispatch(fetchMessages({ channelSid: params.sid }));
            },
            ga: {
                onSubmitMessage: ['sent class message', 'class: sent message']
            },
            isOffline: selectors.getIsOffline(state),
            includeEveryoneMention: true,
            rolePermissions,
            rolesInteractions,
            role: selectors.getCurrentUserRole(state),
            chatText: selectors.getChatTextBySid(state, params.sid) || '',
            sid: params.sid,
            announcementConversation: null,
            isAnnouncement: false,
            type: 'group',
            useProfanityFilter: selectors.getShouldUseProfanityFilter(state),
            conversationStarters: selectors.getAllConversationStarters(state),
            userIsInClass: selectors.currentUserIsInClass(state, classId),
            currentUser,
            getCurrentListeningChannel: handlers.communication.getCurrentChannelSid,
            startListeningChannel: handlers.communication.startListening,
            stopListeningChannel: handlers.communication.stopListening,
            collapseHeaders: selectors.getCollapseHeaders_forGroups(state),
            hasPrevPage: selectors.getHasPrevPage(state),
            schoolEnableReactions: selectors.getSchoolEnableReactions(state),
            schoolEnableGroupGifs: selectors.getSchoolEnableGroupGifs(state),
            debug: selectors.getDebug(state).chat
        };

        return {
            ...frameProps,
            canLoadChat: canLoadClassChat(frameProps),
            dependenciesReady: hasClassChatDependencies(frameProps)
        };
    }, {
        onSubmitMessage: actions.communication.sendMessage,
        onSaveMessage: actions.communication.updateMessageBody,
        onModerateMessage: actions.communication.moderateMessage,
        onPinMessage: actions.communication.pinMessage,
        onRemoveMessage: actions.communication.removeMessage,
        onRemoveOwnMessage: actions.communication.removeOwnMessage,
        onFlagInappropriate: actions.communication.flagMessageAsInappropriate,
        onSendPrivateMessage: (userId) => actions.router.push('/app/messaging/u:' + userId),
        onSendConnectionRequest: ({ userId }) => actions.communication.requestPeer({ userId }),
        onAcceptPeer: ({ userId, firstName }) => actions.communication.acceptPeer({ userId, firstName }),
        onAddReaction: actions.chats.addReaction,
        onRemoveReaction: actions.chats.removeReaction,
        setLastReadMessageIndex_byConversationSid: actions.communication.setLastReadMessageIndex_byConversationSid,
        showNotification: (msg) => actions.alerts.notification.push({ message: msg }),
        setCollapseHeaders: (setting) => actions.chats.setCollapseHeadersGroups({ setting }),
        onUploadChatImage: actions.communication.uploadChatImage,
        onRefreshClass: actions.dataFetching.fetchClass
    }
)(internals.ClassChatFrame);

ClassChatFrame.dependencyPropTypes = dependencyPropTypes;

module.exports = ClassChatFrame;

if (process.env.NODE_ENV === 'test') {
    module.exports._internals = internals;
}
